import * as InvestmentsActions from './actions'
import { AxiosResponse } from 'axios'
import { InvestmentServices } from 'services'
import { getUserBalance } from 'store/transfers/thunks'
import * as datefns from 'date-fns'
import { ThunkDispatchType } from 'store'
import { setShowDrawerError, setShowDrawerSuccess } from 'store/modals/actions'

const formatDate = (date: Date, format: string) => datefns.format(date, format)

export function numberWithCommas(x: number) {
  if (!x) {
    return '0'
  }
  return x.toLocaleString('es-AR')
}

export const rescueInvestment =
  (amount: number, isTotalRescue: boolean) =>
    async (dispatch: ThunkDispatchType): Promise<boolean> => {
      try {
        dispatch(InvestmentsActions.setRescuePending())
        await InvestmentServices.rescueInvestmentService(amount, isTotalRescue)
        dispatch(InvestmentsActions.setRescueFulfilled())
        Promise.all([dispatch(getOrders()), dispatch(getUserBalance())])
        return true
      } catch (error) {
        dispatch(InvestmentsActions.setRescueRejected())
        return false
      }
    }

export const subscribeInvestment =
  (amount: number) =>
    async (dispatch: ThunkDispatchType): Promise<boolean> => {
      try {
        dispatch(InvestmentsActions.setSubscriptionPending())
        await InvestmentServices.subscribeInvestmentService(amount)
        dispatch(InvestmentsActions.setSubscriptionFulfilled())
        Promise.all([dispatch(getOrders()), dispatch(getUserBalance())])
        return true
      } catch (error) {
        dispatch(InvestmentsActions.setSubscriptionRejected())
        return false
      }
    }

export const setPerformance = () => async (dispatch: ThunkDispatchType) => {
  const idClient = await localStorage.getItem('clientId')
  try {
    dispatch(InvestmentsActions.setInvestmentsLoading())
    const response: AxiosResponse<unknown> =
      await InvestmentServices.getPerformanceByClientId(idClient || '')
    const { performance } = (response.data as { performance: unknown })
    dispatch(InvestmentsActions.setPerformance(performance))
    dispatch(InvestmentsActions.dismissInvestmentsLoading())
  } catch (error) {
    dispatch(InvestmentsActions.dismissInvestmentsLoading())
  }
}

export const getTutorialCompletion =
  () => async (dispatch: ThunkDispatchType) => {
    try {
      dispatch(InvestmentsActions.getTutorialCompletionPending())

      const response = await InvestmentServices.getTutorialCompletion()

      if (response.status !== 200) {
        throw response?.data.data
      }

      dispatch(
        InvestmentsActions.getTutorialCompletionFulfilled(response.data.data)
      )
    } catch (error) {
      dispatch(InvestmentsActions.getTutorialCompletionRejected())
    }
  }

export const getIsTutorialCompleted =
  () => async (dispatch: ThunkDispatchType) => {
    try {
      dispatch(InvestmentsActions.getIsTutorialCompletedPending())

      const response = await InvestmentServices.getIsTutorialCompleted()

      if (response.status !== 200) {
        throw response?.data.data
      }

      dispatch(
        InvestmentsActions.getIsTutorialCompletedFulfilled(response.data.data)
      )
    } catch (error) {
      dispatch(InvestmentsActions.getIsTutorialCompletedRejected())
    }
  }

export const getInvestmentClient =
  () => async (dispatch: ThunkDispatchType) => {
    try {
      dispatch(InvestmentsActions.getInvestmentClientPending())
      const response = await InvestmentServices.getInvestmentClient()

      if (response.status !== 200) {
        throw response?.data.data
      }

      dispatch(
        InvestmentsActions.getInvestmentClientFulfilled(response.data.data)
      )
    } catch (error) {
      dispatch(InvestmentsActions.getInvestmentClientRejected())
    }
  }

export const getHoldingAccount = () => async (dispatch: ThunkDispatchType) => {
  try {
    dispatch(InvestmentsActions.getHoldingAccountPending())
    const response = await InvestmentServices.getHoldingAccount()

    if (response.status !== 200) {
      throw response?.data.data
    }
    const amount = numberWithCommas(
      Number(response.data.data.current_position_value)
    )
    dispatch(InvestmentsActions.getHoldingAccountFulfilled(amount))
  } catch (error) {
    dispatch(InvestmentsActions.getHoldingAccountRejected())
  }
}

export const getLastPerformance = () => async (dispatch: ThunkDispatchType) => {
  try {
    dispatch(InvestmentsActions.getLastPerformancePending())
    const response = await InvestmentServices.getLastPerformance()

    if (response.status !== 200) {
      throw response?.data.data
    }

    dispatch(InvestmentsActions.getLastPerformanceFulfilled(response.data.data))
  } catch (error) {
    dispatch(InvestmentsActions.getLastPerformanceRejected())
  }
}

export const getOrders =
  (
    limit?: number | undefined,
    setRefreshing?: (parameter: boolean) => void,
    timeStamp = { dateFrom: new Date(), dateTo: new Date(), daysBefore: 0 },
    filterDate = false
  ) =>
    async (dispatch: ThunkDispatchType) => {
      const { dateFrom, dateTo, daysBefore } = timeStamp
      try {
        const formattedDateFrom = formatDate(dateFrom, 'yyyy-MM-dd')
        const formattedDateTo = formatDate(dateTo, 'yyyy-MM-dd')
        const body = {
          page: 1,
          limit,
          daysBefore,
          dateFrom: formattedDateFrom,
          dateTo: formattedDateTo,
          filterDate: filterDate
        }
        dispatch(InvestmentsActions.getOrdersPending())
        const response = await InvestmentServices.getOrders(body)
        if (response.status !== 200) {
          throw response?.data.data
        }
        dispatch(InvestmentsActions.getOrdersFulfilled(response.data.data))
        if (setRefreshing) {
          setRefreshing(false)
        }
      } catch (error: unknown) {
        dispatch(InvestmentsActions.getOrdersRejected())
      }
    }

export const getFund = () => async (dispatch: ThunkDispatchType) => {
  try {
    dispatch(InvestmentsActions.getFundPending())
    const response = await InvestmentServices.getFund()
    if (response.status !== 200) {
      throw response?.data.data
    }
    dispatch(InvestmentsActions.getFundFulfilled(response.data.data))
  } catch (error) {
    dispatch(InvestmentsActions.getFundRejected())
  }
}

export const getInvestmentParams =
  () => async (dispatch: ThunkDispatchType) => {
    try {
      dispatch(InvestmentsActions.getInvestmentParamsPending())
      const response = await InvestmentServices.getInvestmentParams()

      if (response.status !== 200) {
        throw response?.data.data
      }
      dispatch(
        InvestmentsActions.getInvestmentParamsFulfilled(response.data.data)
      )
    } catch (error) {
      dispatch(InvestmentsActions.getInvestmentParamsRejected())
    }
  }

export const cancelInvestmentOrder =
  (id: number, onClose: () => void, isSubscription: boolean) =>
    async (dispatch: ThunkDispatchType) => {
      const operationText = isSubscription ? 'la suscripción' : 'el rescate'
      const operationTitle = isSubscription ? 'Suscripción' : 'Rescate'
      try {
        dispatch(InvestmentsActions.setCancelInvestmentOrderPending())
        const response = await InvestmentServices.cancelInvestmentOrder(id)
        Promise.all([dispatch(getOrders()), dispatch(getUserBalance())])
        if (response.status !== 200) {
          throw new Error()
        }
        dispatch(
          InvestmentsActions.setCancelInvestmentOrderFulfilled(response.data.data)
        )
        onClose()
        dispatch(
          setShowDrawerSuccess(
            true,
            true,
            '¡Listo!',
            `Cancelaste correctamente ${operationText}`,
            {
              fromScreen: 'investments',
              title: `${operationTitle}`
            },
            true
          )
        )
      } catch (error) {
        dispatch(InvestmentsActions.setSubscriptionRejected())
        onClose()
        dispatch(
          setShowDrawerError(
            true,
            `No se pudo cancelar tu ${operationText}.`,
            '',
            {
              fromScreen: 'investments'
            }
          )
        )
      }
    }

export const downloadSummaryMovements =
  (body: { dateFrom: string; dateTo: string }) =>
    async (dispatch: ThunkDispatchType) => {
      try {
        dispatch(InvestmentsActions.setInvestmentsLoading())
        const response = await InvestmentServices.downloadSummaryMovements(body)

        if (response.status !== 200) {
          throw new Error()
        }
        dispatch(InvestmentsActions.dismissInvestmentsLoading())
        return response.data.data.data
      } catch (error) {
        dispatch(InvestmentsActions.dismissInvestmentsLoading())
        console.log(error)
      }
    }
